/**
 * Hazard visuals. Each hazard is drawn as the lethal material between the wall and its inner
 * silhouette — and that inner silhouette IS the kind's `innerRadius` (the same function the SDF uses),
 * so what you see is what kills you (FS-06 R08). The guaranteed node is drawn as a clear gap plus a
 * bright node glyph (Pillar P2 readability).
 */
import * as THREE from 'three';
import { TUNNEL_RADIUS } from '../core/constants';
import { angularDelta } from '../core/math-det';
import { RING_THICKNESS, type HazardKindDef, type HazardParams } from '../game/hazards/types';
import type { Hazard } from '../game/hazards/instance';
import { hueColor } from './palette';

const SEG = 96;
const TAU = Math.PI * 2;

/** The inner and outer radius of the drawn lethal band at one bearing. */
export interface BandRadii {
  inner: number;
  outer: number;
}

/**
 * **The one place that decides what a hazard looks like.** Silhouette == hitbox (FS-06 R08, and the
 * repo's invariant 5): the band's inner edge is the kind's own `innerRadius`, run through the same
 * polarity mirror and `reach` that `signedDistance` applies, so an axis hazard cannot kill from
 * material the player cannot see.
 *
 * The live renderer calls this per segment per frame; the How to Play screen calls it to draw its
 * hazard-family illustrations (FS-12 R35 — the card's image is the game's own geometry, so it cannot
 * drift from what the game draws). Pure and allocation-free: the caller owns `out`.
 */
export function bandRadiiAt(
  def: HazardKindDef,
  p: HazardParams,
  theta: number,
  tLocal: number,
  nodeTheta: number,
  out: BandRadii,
): void {
  const isRing = def.silhouette === 'concentric-ring' || def.silhouette === 'nested-echo';
  if (isRing) {
    const rBase = def.innerRadius(p, 0, tLocal);
    out.outer = Math.min(rBase + RING_THICKNESS, TUNNEL_RADIUS);
    out.inner = Math.max(rBase - RING_THICKNESS, 0.1);
    return;
  }
  const surface = def.innerRadius(p, theta, tLocal);
  const inNode = p.nodeHalfTheta > 0 && Math.abs(angularDelta(theta, nodeTheta)) < p.nodeHalfTheta;
  if (p.polarity === 'axis') {
    // Material grows from the axis outward to the mirrored, reached surface.
    const reached = (TUNNEL_RADIUS - surface) * p.reach;
    out.outer = inNode ? 0.2 : Math.min(Math.max(reached, 0.2), TUNNEL_RADIUS);
    out.inner = 0.2;
  } else {
    const reached = TUNNEL_RADIUS + (surface - TUNNEL_RADIUS) * p.reach;
    out.outer = TUNNEL_RADIUS;
    out.inner = inNode ? TUNNEL_RADIUS : Math.min(Math.max(reached, 0.2), TUNNEL_RADIUS);
  }
}

function buildIndices(): number[] {
  const idx: number[] = [];
  for (let i = 0; i < SEG; i++) {
    const o0 = 2 * i;
    const i0 = 2 * i + 1;
    const o1 = 2 * (i + 1);
    const i1 = 2 * (i + 1) + 1;
    idx.push(o0, i0, i1, o0, i1, o1);
  }
  return idx;
}
const INDICES = buildIndices();

/**
 * `[ ]` — two brackets in unit space, straddling x = 0. Scaled to the node's arc width so the pair
 * reads as "the gap is this wide", which is what FS-06 R22 asks the glyph to communicate.
 */
function buildBracketPairGeometry(): THREE.BufferGeometry {
  const T = 0.34; // bracket tick length, in units of node half-width
  const H = 0.5; // bracket half-height
  const pts: number[] = [];
  for (const side of [-1, 1]) {
    const x = side * 0.5;
    const xi = side * (0.5 - T);
    // vertical stroke
    pts.push(x, -H, 0, x, H, 0);
    // two ticks turning inward, toward the passable gap
    pts.push(x, H, 0, xi, H, 0);
    pts.push(x, -H, 0, xi, -H, 0);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  return g;
}

class HazardVisual {
  readonly group = new THREE.Group();
  private positions = new Float32Array((SEG + 1) * 2 * 3);
  private edgePositions = new Float32Array((SEG + 1) * 3);
  /** Module-scratch band radii — the rebuild loop must not allocate (invariant 8). */
  private band: BandRadii = { inner: 0, outer: 0 };
  private fillGeo = new THREE.BufferGeometry();
  private edgeGeo = new THREE.BufferGeometry();
  private fillMat: THREE.MeshBasicMaterial;
  private edgeMat: THREE.LineBasicMaterial;
  private glyph: THREE.LineSegments;

  constructor() {
    this.fillGeo.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.fillGeo.setIndex(INDICES);
    this.fillMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.22,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.group.add(new THREE.Mesh(this.fillGeo, this.fillMat));

    this.edgeGeo.setAttribute('position', new THREE.BufferAttribute(this.edgePositions, 3));
    this.edgeMat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.95 });
    this.group.add(new THREE.LineLoop(this.edgeGeo, this.edgeMat));

    // FS-06 §6.3 / R22 — the node glyph is a **bracket pair** marking the node's angular extent.
    // It was a solid filled diamond, which is the shape language of a collectable: playtest read it
    // as something to grab rather than a gate to fly through. Brackets frame a gap; they cannot be
    // mistaken for an object. This also frees the diamond silhouette for the one thing that IS
    // collectable (FS-07 §5.5).
    this.glyph = new THREE.LineSegments(
      buildBracketPairGeometry(),
      new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.95 }),
    );
    this.group.add(this.glyph);
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  update(h: Hazard, alpha: number, echoPilot: boolean): void {
    const z = h.prevZ + (h.z - h.prevZ) * alpha;
    this.group.position.z = z;

    const isRing = h.def.silhouette === 'concentric-ring' || h.def.silhouette === 'nested-echo';
    const p = h.params;
    const t = h.tLocal;
    const nodeTheta = h.nodeTheta; // moving for HelixBraid / ChirpSweep

    const pos = this.positions;
    const edge = this.edgePositions;
    const band = this.band;
    for (let i = 0; i <= SEG; i++) {
      const theta = (i / SEG) * TAU;
      const sx = Math.sin(theta);
      const cy = Math.cos(theta);

      bandRadiiAt(h.def, p, theta, t, nodeTheta, band);

      const b = i * 6;
      pos[b] = sx * band.outer;
      pos[b + 1] = cy * band.outer;
      pos[b + 2] = 0;
      pos[b + 3] = sx * band.inner;
      pos[b + 4] = cy * band.inner;
      pos[b + 5] = 0;

      const e = i * 3;
      edge[e] = sx * band.inner;
      edge[e + 1] = cy * band.inner;
      edge[e + 2] = 0;
    }
    this.fillGeo.attributes.position.needsUpdate = true;
    this.edgeGeo.attributes.position.needsUpdate = true;
    this.fillGeo.computeBoundingSphere();

    // Depth cue: dim + telegraph fade-in as it approaches from far.
    const depth = Math.min(1, (z + 240) / 240);
    const near = 1 - Math.min(1, Math.max(0, (z - -60) / 60)) * 0; // keep full near
    void near;
    const approach = Math.min(1, Math.max(0, (z + 240) / 60)); // 0 far → 1 by z≈-180
    const alive = h.lethal ? 1 : Math.max(0, 1 - h.dissolve);
    // FS-01 R08 — a Phantom is **outline without fill**. Distinct by outline and fill, never by hue
    // alone (Pillar P6), so it survives greyscale and every colour-vision deficiency: the lethal
    // transition at 16.3 s is a solid arriving where there was only an edge. A hazard mid-dissolve
    // (Wavebreaker) is not a phantom — it is fading, and `alive` already says so.
    const phantom = !h.lethal && h.dissolve === 0;

    const col = hueColor(h.def.hue, 0.85, 0.6);
    this.fillMat.color.copy(col);
    this.edgeMat.color.copy(hueColor(h.def.hue, 0.9, 0.72));
    this.fillMat.opacity = phantom ? 0 : (0.1 + 0.16 * depth) * approach * alive;
    this.edgeMat.opacity = (0.5 + 0.45 * depth) * approach * alive * (phantom ? 0.75 : 1);

    // Node glyph at the safe arc (angular kinds). Scale up with distance for ~constant screen size.
    if (isRing || p.nodeHalfTheta <= 0) {
      this.glyph.visible = false;
    } else {
      this.glyph.visible = true;
      const gr = TUNNEL_RADIUS * 0.5;
      this.glyph.position.set(Math.sin(nodeTheta) * gr, Math.cos(nodeTheta) * gr, 0.1);
      const gs = (0.9 + (-z / 240) * 3.0) * (echoPilot ? 1.6 : 1);
      const scale = Math.max(0.6, gs);
      // Width tracks the node's real arc so the brackets frame the actual gap; height stays at
      // constant screen size like any other glyph (FS-03 R41).
      const arcW = Math.max(2 * p.nodeHalfTheta * gr, scale);
      this.glyph.scale.set(arcW, scale, 1);
      // Brackets are tangential to the tunnel, so they sit square across the gap.
      this.glyph.rotation.z = -nodeTheta;
      const gm = this.glyph.material as THREE.LineBasicMaterial;
      // Echo Pilot lights the optimal path — the node glyphs (Design Bible §9.1 #6).
      gm.color.copy(echoPilot ? new THREE.Color(0xffffff) : hueColor(h.def.hue, 0.6, 0.85));
      gm.opacity = (echoPilot ? 1.0 : 0.85 * approach) * alive;
    }
  }
}

export class HazardRenderer {
  readonly group = new THREE.Group();
  private pool: HazardVisual[] = [];

  constructor(capacity: number) {
    for (let i = 0; i < capacity; i++) {
      const v = new HazardVisual();
      v.setVisible(false);
      this.pool.push(v);
      this.group.add(v.group);
    }
  }

  /** Sync visuals to the live hazards for this frame. */
  sync(live: readonly Hazard[], liveCount: number, alpha: number, echoPilot: boolean): void {
    for (let i = 0; i < this.pool.length; i++) {
      const v = this.pool[i]!;
      if (i < liveCount) {
        v.setVisible(true);
        v.update(live[i]!, alpha, echoPilot);
      } else {
        v.setVisible(false);
      }
    }
  }
}
